import { z } from 'zod';

/**
 * OnPass create-form schema. Mirrors the server-side validation in
 * SecretsFunctions.ValidateAndNormalise: secret required (≤100k chars),
 * MaxViews optional 1–100 (blank = unlimited until expiry), an expiry DATE
 * within 30 days, optional passphrase. The lock-to-a-user selection and the
 * expiry are handled as an absolute instant when the form is submitted.
 */
export const MAX_SECRET_LENGTH = 100_000;
export const MAX_VIEWS_CAP = 100;
export const MAX_DAYS_TO_LIVE = 30;

/** Local midnight today, for date-only comparisons (no time-zone drift). */
function todayMidnight(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

/** Parse a yyyy-mm-dd value to a local Date at midnight; null if unparseable. */
function parseDateOnly(s: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (!m) return null;
  const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Default expiry date (today + 14 days) as a yyyy-mm-dd string for the picker. */
export function defaultExpiryDate(): string {
  const d = todayMidnight();
  d.setDate(d.getDate() + 14);
  return toDateInputValue(d);
}

/** The earliest / latest selectable expiry dates (today .. today + 30) as yyyy-mm-dd. */
export function minExpiryDate(): string {
  return toDateInputValue(todayMidnight());
}
export function maxExpiryDate(): string {
  const d = todayMidnight();
  d.setDate(d.getDate() + MAX_DAYS_TO_LIVE);
  return toDateInputValue(d);
}

/** Date → yyyy-mm-dd (local), the value shape a <input type="date"> uses. */
export function toDateInputValue(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/**
 * A chosen expiry date (yyyy-mm-dd) → the absolute UTC instant to send. The link
 * expires at the END of the chosen local day, so a date picked today still gives
 * a usable window.
 */
export function expiryDateToIsoUtc(dateOnly: string): string | undefined {
  const d = parseDateOnly(dateOnly);
  if (!d) return undefined;
  d.setHours(23, 59, 59, 999);
  return d.toISOString();
}

export const createSecretSchema = z.object({
  Secret: z
    .string()
    .min(1, 'A secret is required')
    .max(MAX_SECRET_LENGTH, `Secret must be ${MAX_SECRET_LENGTH.toLocaleString()} characters or fewer`),
  // Optional: blank means "unlimited views until it expires".
  MaxViews: z.preprocess(
    (v) => (v === '' || v === null || v === undefined ? undefined : v),
    z.coerce
      .number({ invalid_type_error: 'Enter a number' })
      .int('Whole numbers only')
      .min(1, 'At least 1 view')
      .max(MAX_VIEWS_CAP, `At most ${MAX_VIEWS_CAP} views`)
      .optional(),
  ),
  ExpiresAt: z
    .string()
    .min(1, 'Choose an expiry date')
    .superRefine((s, ctx) => {
      const d = parseDateOnly(s);
      if (!d) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'Choose a valid date' });
        return;
      }
      if (d < todayMidnight()) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'The expiry date must be today or later' });
      }
      const cap = todayMidnight();
      cap.setDate(cap.getDate() + MAX_DAYS_TO_LIVE);
      if (d > cap) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: `The expiry date must be within ${MAX_DAYS_TO_LIVE} days` });
      }
    }),
  Passphrase: z.string().optional(),
});

export type CreateSecretFormData = z.infer<typeof createSecretSchema>;
